import { UI_FIELD_TYPES } from '@optidev/shared-types';
import type { AuthMethod, UIField } from '@optidev/shared-types';

/**
 * Layout rules for the schema-driven connection form.
 *
 * The long connector forms are long because every field carries a permanent line of helper text
 * and every optional field is always expanded. Nothing here removes content — it arranges it:
 *
 * - required vs optional comes from `field.required`; optional fields move into "Advanced"
 * - the helper lines are collected from `field.description` into one disclosure per form
 * - section headers only appear when a form is big enough to need them, so a two-field
 *   connector (MongoDB) still looks like a two-field connector
 *
 * All of it is derived from the existing schema, so no connector definition changes.
 */

/** Fields whose value is short enough to sit next to its neighbour on a wide dialog. */
const NARROW_FIELD_NAMES = new Set([
  'port',
  'catalog',
  'schema',
  'engine',
  'region',
  'version',
  'timeout',
]);

/**
 * Field names that identify a credential even when the input type does not. Deliberately does not
 * match a bare "account": Snowflake's `account` is an address, not a secret, and belongs with the
 * connection details.
 */
const CREDENTIAL_NAME_PATTERN =
  /(password|passwd|secret|token|apikey|api_key|accesskey|access_key|privatekey|private_key|credential|serviceaccount|service_account|user|login|email)/i;

/**
 * Below this many required fields a form is short enough to read in one pass, so it gets no
 * section headers at all — two headers over three inputs is chrome, not structure.
 */
const MIN_FIELDS_FOR_SECTIONS = 3;

/** Types that can share a row — anything taller than one line stays full width. */
const COMPACT_FIELD_TYPES: readonly string[] = [
  UI_FIELD_TYPES.TEXT,
  UI_FIELD_TYPES.NUMBER,
  UI_FIELD_TYPES.SELECT,
  UI_FIELD_TYPES.PASSWORD,
];

/** Types that own their description (checkbox uses it as its label, picker renders its own). */
const SELF_DESCRIBING_FIELD_TYPES: readonly string[] = [
  UI_FIELD_TYPES.CHECKBOX,
  UI_FIELD_TYPES.RESOURCE_PICKER,
];

/** A form section — "Connection" / "Authentication" — or the single implicit group. */
export interface FieldSection {
  id: 'connection' | 'authentication';
  title: string;
  fields: UIField[];
}

/** One row of the field grid: a single full-width field, or two side by side. */
export type FieldRow = UIField[];

export interface InstructionRow {
  label: string;
  text: string;
}

/** Label for the connector-wide setup note, so the title logic can recognise it. */
const SETUP_NOTES_LABEL = 'Setup notes';

export interface ConnectionFormLayout {
  /** Required fields, split into sections when the form is large enough to warrant headers. */
  sections: FieldSection[];
  /** Optional fields, collapsed behind "Advanced". */
  advancedFields: UIField[];
  /** Every line of per-field instruction, gathered into one disclosure. */
  instructions: InstructionRow[];
  /** Header for that disclosure, matched to what is actually inside it. */
  instructionsTitle: string;
  /** Whether to render section headers at all (a short form stays flat). */
  useSections: boolean;
}

function isCredentialField(field: UIField): boolean {
  return field.type === UI_FIELD_TYPES.PASSWORD || CREDENTIAL_NAME_PATTERN.test(field.name);
}

function isNarrowField(field: UIField): boolean {
  return field.type === UI_FIELD_TYPES.NUMBER || NARROW_FIELD_NAMES.has(field.name);
}

function isCompactField(field: UIField): boolean {
  return COMPACT_FIELD_TYPES.includes(field.type);
}

/** Descriptions the disclosure owns. Self-describing types keep theirs inline. */
export function hasCollectedDescription(field: UIField): boolean {
  return Boolean(field.description) && !SELF_DESCRIBING_FIELD_TYPES.includes(field.type);
}

/**
 * Pack fields into rows, pairing a narrow field (port, catalog, schema) with the compact field
 * before it. Order is never changed — a narrow field with a tall neighbour just stays full width.
 */
export function buildFieldRows(fields: UIField[]): FieldRow[] {
  const rows: FieldRow[] = [];

  for (const field of fields) {
    const previousRow = rows[rows.length - 1];
    const canPair =
      previousRow?.length === 1 &&
      isCompactField(previousRow[0]) &&
      isCompactField(field) &&
      isNarrowField(field);

    if (canPair) {
      previousRow.push(field);
    } else {
      rows.push([field]);
    }
  }

  return rows;
}

/**
 * Derive the whole layout from an auth method's fields.
 *
 * Credentials are split into their own "Authentication" section only when there is something
 * left in "Connection" — a connector whose one required field is a connection URI keeps a
 * single section rather than showing a header over one input.
 */
export function buildConnectionFormLayout(
  fields: UIField[],
  authMethod?: AuthMethod,
  schemaHelpText?: string,
): ConnectionFormLayout {
  // Resource pickers are the point of the form when present, never "advanced".
  const advancedFields = fields.filter(
    (field) => !field.required && field.type !== UI_FIELD_TYPES.RESOURCE_PICKER,
  );
  const primaryFields = fields.filter((field) => !advancedFields.includes(field));

  const credentialFields = primaryFields.filter(isCredentialField);
  const connectionFields = primaryFields.filter((field) => !credentialFields.includes(field));

  const splitCredentials =
    credentialFields.length > 0 &&
    connectionFields.length > 0 &&
    primaryFields.length >= MIN_FIELDS_FOR_SECTIONS;

  const sections: FieldSection[] = splitCredentials
    ? [
        { id: 'connection', title: 'Connection', fields: connectionFields },
        { id: 'authentication', title: 'Authentication', fields: credentialFields },
      ]
    : [{ id: 'connection', title: 'Connection', fields: primaryFields }];

  const instructions: InstructionRow[] = [];
  /*
   * Setup guidance that is about the connector rather than any one field: the schema-level note
   * (on Grafana and AWS this paragraph is taller than the form) and the auth method's extra help.
   * Both share one label — labelling them with the method's own name produced a row called
   * "Connection URI" or "Personal Access Token" sitting right next to the field of the same name.
   *
   * The method's one-line `description` is deliberately absent: it explains which tab you are on,
   * so it stays inline under the tabs, where it only renders when there is more than one tab.
   */
  if (schemaHelpText) {
    instructions.push({ label: SETUP_NOTES_LABEL, text: schemaHelpText });
  }
  if (authMethod?.helpText) {
    instructions.push({ label: SETUP_NOTES_LABEL, text: authMethod.helpText });
  }
  for (const field of fields) {
    if (hasCollectedDescription(field)) {
      instructions.push({ label: field.label, text: field.description as string });
    }
  }

  /**
   * Headers exist to tell two groups apart, so they only appear when there are two. A single
   * group needs no label — and labelling it guesses wrong on the connectors that are nothing but
   * an API key ("Connection") or a lone connection URI ("Authentication"). "Advanced" carries its
   * own header, so the boundary below is never ambiguous.
   */
  const useSections = splitCredentials;

  /**
   * The title has to answer for what is actually inside. "these" is wrong over a single row, and
   * the whole question is wrong when the only row is a connector-wide note rather than a
   * where-to-find-it answer.
   */
  const fieldHelpCount = instructions.filter((row) => row.label !== SETUP_NOTES_LABEL).length;
  const instructionsTitle =
    fieldHelpCount === 0
      ? SETUP_NOTES_LABEL
      : fieldHelpCount === 1 && instructions.length === 1
        ? 'Where do I find this?'
        : 'Where do I find these?';

  // With nothing but notes inside, the header already says "Setup notes" — repeating it as a row
  // label is a stutter, so those rows drop their label and run full width.
  const labelledInstructions =
    fieldHelpCount === 0 ? instructions.map((row) => ({ ...row, label: '' })) : instructions;

  return {
    sections,
    advancedFields,
    instructions: labelledInstructions,
    instructionsTitle,
    useSections,
  };
}

/**
 * Labels for the "Advanced" header. Some connectors already end a label with "(optional)", which
 * reads as a stutter next to the group's own "N optional" count.
 */
export function advancedSummaryLabels(fields: UIField[]): string[] {
  return fields.map((field) => field.label.replace(/\s*\(optional\)\s*$/i, '').trim());
}

/** True when any of these fields already holds a value — used to open "Advanced" on edit. */
export function hasAnyValue(
  fields: UIField[],
  credentials: Record<string, unknown>,
  maskedCredentials?: Record<string, unknown>,
): boolean {
  return fields.some((field) => {
    const value = credentials[field.name] ?? maskedCredentials?.[field.name];
    if (value === undefined || value === null || value === '') return false;
    // A checkbox sitting at its default (usually `true` for SSL) is not user intent.
    if (field.type === UI_FIELD_TYPES.CHECKBOX) return value !== field.defaultValue;
    return true;
  });
}
